import { Button, Divider, Grid, Step, Stepper } from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';
import {
  StepperGrid,
  StepperIconTitle,
  StepperLabel,
  StepperTitle,
  StyledDialog,
} from './AppStepperStyled';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsSolid } from 'src/config/api/models/FontAwesome';
import theme from 'src/config/theme';
import { usePostStepperMutation } from 'src/store/stepper/stepperApi';
import { useRequestMeMutation } from 'src/modules/shared/slices/auth/entity/api/auth';
import { User } from 'src/config/api/models/User';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';

export const AppStepper: FC = () => {
  const log = useDataLogging();
  const [nextStep, result] = usePostStepperMutation();
  const [me, { data: meResponse }] = useRequestMeMutation({
    fixedCacheKey: '/me',
  });
  const stepper: User['stepper'] = meResponse?.stepper;
  const [showStepper, setShowStepper] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState(0);
  const [checkActiveStep, setCheckActiveStep] = useState<boolean>(false);

  useEffect(() => {
    setShowStepper(!!stepper?.key);
  }, [stepper?.key]);

  const handleStepper = (step: number) => {
    if (!stepper) return;
    if (activeStep === 0 && step === -1) {
      return;
    } else if (activeStep > 0 && step === -1) {
      log(LogActionTypes.STEPPER_CLICK_PREVIOUS, {
        stepper: meResponse?.stepper?.key,
        currentStep: activeStep + 1,
        nextStep: activeStep + 1 - 1,
      });
      return setActiveStep((prev) => prev - 1);
    }
    log(LogActionTypes.STEPPER_CLICK_NEXT, {
      stepper: meResponse?.stepper?.key,
      currentStep: activeStep + 1,
      nextStep: activeStep + 1 + 1,
    });
    nextStep({ key: stepper?.key, index: activeStep.toString() });
  };

  useEffect(() => {
    if (result.isSuccess) {
      result.reset();
      if (stepper && stepper.steps.length === activeStep + 1) {
        return setShowStepper(false);
      }

      setActiveStep((prev) => prev + 1);
    }
  }, [activeStep, log, meResponse?.stepper?.key, result, stepper]);

  useEffect(() => {
    if (checkActiveStep) return;
    if (!meResponse?.stepper) return;
    if (meResponse?.stepper) {
      setCheckActiveStep(true);
      const steps = meResponse.stepper.steps;
      const firstIncompleteStepIndex = steps.findIndex((step) => !step.isCompleted);

      if (firstIncompleteStepIndex !== -1) {
        setActiveStep(firstIncompleteStepIndex);
      }
      log(LogActionTypes.STEPPER_VIEWED, {
        stepper: meResponse?.stepper?.key,
        step: firstIncompleteStepIndex ? firstIncompleteStepIndex : activeStep + 1,
      });
    }
  }, [meResponse?.stepper, log, checkActiveStep, activeStep]);

  const isLastStep = meResponse?.stepper && activeStep === meResponse.stepper.steps.length - 1;

  useEffect(() => {
    if (!showStepper && isLastStep) {
      me();
    }
  }, [isLastStep, me, showStepper]);

  return (
    <StyledDialog open={showStepper} fullScreen>
      <Stepper activeStep={activeStep}>
        {stepper?.steps.map((label, index) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: { optional?: React.ReactNode } = {};
          return (
            <Step key={index} {...stepProps}>
              <StepperLabel {...labelProps}>{label.title}</StepperLabel>
            </Step>
          );
        })}
      </Stepper>
      <Divider />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          padding: 24,
        }}
      >
        <div>
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <FontAwesomeSpriteIcon
                icon={SpriteIconsSolid.UMBRELLA}
                color={theme.palette.ternary.main}
                iconSize="2xl"
              />
              <StepperIconTitle>{stepper?.label.text}</StepperIconTitle>
            </div>
            <StepperTitle variant="h2">{stepper?.steps[activeStep].contentTitle}</StepperTitle>
          </div>
          <Grid container spacing={2}>
            {stepper?.steps[activeStep].contents.map((item) => {
              return (
                <StepperGrid xs={6} item>
                  <div dangerouslySetInnerHTML={{ __html: item.content }} />
                </StepperGrid>
              );
            })}
          </Grid>
        </div>

        <div style={{ width: '100%', justifyContent: 'flex-end', display: 'flex', gap: 8 }}>
          {activeStep !== 0 && (
            <Button
              onClick={() => handleStepper(-1)}
              variant="text"
              disableElevation
              style={{ textTransform: 'none' }}
              disabled={result.isLoading}
            >
              TERUG
            </Button>
          )}
          <Button
            onClick={() => handleStepper(1)}
            variant="contained"
            color="primary"
            disableElevation
            style={{ textTransform: 'none' }}
            disabled={result.isLoading}
          >
            {isLastStep ? 'ERVAAR HET NU' : 'VOLGENDE'}
          </Button>
        </div>
      </div>
    </StyledDialog>
  );
};
